import { c as _c } from "react/compiler-runtime";
import { type DependencyList, type ReactNode, useEffect } from 'react';
import { create } from 'zustand';
export type OnEscKeydownHandler = (() => void) | OnEscKeydownHandlerWithDeps;

/** The handler function for the provided `OnEscKeydownHandler` */
export const getHandler = (onEscKeydown: OnEscKeydownHandler) => {
  if (typeof onEscKeydown === 'function') {
    return onEscKeydown;
  }
  return onEscKeydown.handler;
};
export const getDeps = (onEscKeydown: OnEscKeydownHandler) => {
  if (typeof onEscKeydown === 'function') {
    return [];
  }
  return onEscKeydown.deps ?? [];
};
export type OnEscKeydownHandlerWithDeps = {
  /** The function to invoke when the user presses Esc. */
  handler: () => void;
  /**
   * The list of dependencies that, upon change, signals that the `handler` has a new value.
   * If your `handler` has any conditional logic, the `deps` passed here should reflect so.
   *
   * These are the same as the `deps` you would pass if you were to add a `useCallback` wrapper around the definition of your `handler` function.
   */
  deps?: DependencyList;
};
type CaptureEscKeydownProps = {
  /** A unique identifier for this `CaptureEscKeydown` comopnent */
  id: string;
  /**
   * The function to invoke when the user presses Esc.
   *
   * This component's `onEscKeydown` is only invoked if this is the top-most `CaptureEscKeydown` component.
   *
   * Optionally includes the list of `deps` that, upon change, signal that the `onEscKeydown` handler has a new value. Used for callback functions with conditional logic.
   */
  onEscKeydown: OnEscKeydownHandler;
};

/**
 * If the user presses escape, this component's `onEscKeydown` is trigger IF AND ONLY IF this is
 * the top-most `CaptureEscKeydown` component.
 *
 * By making this a component, we can use the order in which components are mounted to track which
 * is "top-most" - the most recently mounted component is on top, and the escape button should be
 * captured only by that top-most component.
 */
const CaptureEscKeydown = t0 => {
  const $ = _c(13);
  const {
    id,
    onEscKeydown
  } = t0;
  const {
    pushOrReplaceEscOperation,
    removeEscOperation
  } = useEscOperationStack();
  let t1;
  if ($[0] !== onEscKeydown) {
    t1 = getHandler(onEscKeydown);
    $[0] = onEscKeydown;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onClose = t1;
  let t2;
  if ($[2] !== onEscKeydown) {
    t2 = getDeps(onEscKeydown);
    $[2] = onEscKeydown;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const deps = t2;
  let t3;
  if ($[4] !== id || $[5] !== onClose || $[6] !== pushOrReplaceEscOperation) {
    t3 = () => {
      pushOrReplaceEscOperation({
        id,
        onClose
      });
    };
    $[4] = id;
    $[5] = onClose;
    $[6] = pushOrReplaceEscOperation;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t3, deps);
  let t4;
  if ($[8] !== id || $[9] !== removeEscOperation) {
    t4 = () => () => removeEscOperation(id);
    $[8] = id;
    $[9] = removeEscOperation;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== id) {
    t5 = [id];
    $[11] = id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  return null;
};
type EscOperation = {
  id: string;
  onClose: () => void;
};
type EscOperationStackData = {
  /** Stack of esc operations to handle escape key presses */
  escOperations: EscOperation[];
};
type EscOperationStore = {
  data: EscOperationStackData;
  set: (data: Partial<EscOperationStackData>) => void;
};
export const initialUndoState: EscOperationStackData = {
  /** A stack of operations that should be called when the escape button is hit. As comonents are mounted, they register their `escOperation` here. So the stack contains `escOperations` in the order of component instantiation. */
  escOperations: []
};
const useEscOperationStore = create<EscOperationStore>()(zustandSet => ({
  data: initialUndoState,
  set: (partialData: Partial<EscOperationStackData>) => zustandSet(state => ({
    data: {
      ...state.data,
      ...partialData
    }
  }))
}));

// Provider component for testing - allows setting initial store state
export const EscOperationStackProvider = t0 => {
  const $ = _c(5);
  const {
    children,
    initialData
  } = t0;
  let t1;
  let t2;
  if ($[0] !== initialData) {
    t1 = () => {
      if (initialData) {
        useEscOperationStore.getState().set(initialData);
      }
    };
    t2 = [initialData];
    $[0] = initialData;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== children) {
    t3 = <>{children}</>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const useEscOperationStack = () => {
  const $ = _c(5);
  const pushOrReplaceEscOperation = _temp;
  const removeEscOperation = _temp2;
  const {
    data: t0
  } = useEscOperationStore();
  const {
    escOperations: escOperations_1
  } = t0;
  const t1 = escOperations_1.length > 0;
  let t2;
  if ($[0] !== escOperations_1) {
    t2 = () => {
      escOperations_1[escOperations_1.length - 1]?.onClose();
    };
    $[0] = escOperations_1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      pushOrReplaceEscOperation,
      removeEscOperation,
      hasEscOperation: t1,
      onEscKeyDown: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export default CaptureEscKeydown;
function _temp(operation) {
  const state = useEscOperationStore.getState();
  const escOperations = [...state.data.escOperations];
  if (escOperations.some(o_0 => o_0.id === operation.id)) {
    const idx = escOperations.findIndex(o => o.id === operation.id);
    escOperations[idx] = operation;
  } else {
    escOperations.push(operation);
  }
  state.set({
    escOperations
  });
}
function _temp2(id) {
  const state_0 = useEscOperationStore.getState();
  const escOperations_0 = state_0.data.escOperations.filter(o_1 => o_1.id !== id);
  state_0.set({
    escOperations: escOperations_0
  });
}